'use client'

import { useMutation } from '@apollo/client'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@chain-zen/ui'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { ENABLE_TRUCKER_TOOLS_CARRIER_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { TCarrier } from '../../../types/apps'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'
import { TruckerToolsCarrierUpdateModal } from './trucker-tools-carrier-update-modal'
import { getTruckerToolsGroupStructure, getTruckerToolsPayloadStructure } from './trucker-tools-modal-utils'

type TFieldValues = {
  [key: string]: boolean | string
}

interface ICustomField {
  disabled?: boolean
  helpText?: string
  integrationConfigurationID?: string
  isRequired?: boolean
  labelText: string
  name: string
  placeholder?: string
}

interface ITruckerToolsCarrierEnableAppInput {
  appType: AppTypeEnum
  carriers: TCarrier[]
  clientID: string
  clientSecret: string
}

interface ICarrierGroups {
  [key: string]: ICustomField[]
}

export function TruckerToolsCarrierModal({
  appType,
  isOpen,
  isModalEditable,
  setModalEditable,
  app,
  onClose
}: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')
  const [carriers, setCarriers] = useState<ICarrierGroups>(() => getTruckerToolsGroupStructure(1))
  const [deletedGroups, setDeletedGroups] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<TFieldValues>({
    defaultValues: {
      clientID: '',
      clientSecret: ''
    }
  })
  const [customDetailsEnabledByGroup, setCustomDetailsEnabledByGroup] = useState<Record<string, boolean>>({})

  const removeFieldsFromSchema = (id: string) => {
    setDeletedGroups((prev) => [...prev, id])
  }

  const handleAddMore = () => {
    const uniqueID = uuidv4()
    const newlyAddedGroup = getTruckerToolsGroupStructure(uniqueID)
    setCarriers((old) => ({
      ...old,
      ...newlyAddedGroup
    }))
    setCustomDetailsEnabledByGroup((old) => ({ ...old, [uniqueID]: false }))
  }

  const closeModal = () => {
    setFormError('')
    reset()
    onClose()
    if (setModalEditable) {
      setModalEditable(false)
    }
  }

  const handleGroupDelete = (groupID: string) => {
    if (Object.keys(carriers).length > 1) {
      removeFieldsFromSchema(groupID)
      setCarriers((old) => {
        const newGroups = { ...old }
        delete newGroups[groupID]
        return newGroups
      })
      setCustomDetailsEnabledByGroup((old) => {
        const updated = { ...old }
        delete updated[groupID]
        return updated
      })
    }
  }

  const handleCustomDetailsToggle = (groupID: string, enabled: boolean) => {
    setCustomDetailsEnabledByGroup((old) => ({ ...old, [groupID]: enabled }))
    if (!enabled) {
      setValue(`overrideBrokerMC-${groupID}`, '')
      setValue(`overrideDotNumber-${groupID}`, '')
      setValue(`overrideMcNumber-${groupID}`, '')
      setValue(`overrideScacCode-${groupID}`, '')
    }
  }

  const [enableTruckerToolsCarrierApp, { loading }] = useMutation<
    { enableTruckerToolsCarrierApp: { id: string } },
    ITruckerToolsCarrierEnableAppInput
  >(ENABLE_TRUCKER_TOOLS_CARRIER_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('Trucker Tools Carrier app is enabled successfully!')
      closeModal()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Client authentication failed'
          ? 'Client authentication failed. Please check your Client ID and Client Secret.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleSaveDetails = (data: TFieldValues) => {
    const formData = { ...data }
    deletedGroups.forEach((id) => {
      delete formData[`brokerMC-${id}`]
      delete formData[`dotNumber-${id}`]
      delete formData[`mcNumber-${id}`]
      delete formData[`provider-${id}`]
      delete formData[`scacCode-${id}`]
      delete formData[`overrideBrokerMC-${id}`]
      delete formData[`overrideDotNumber-${id}`]
      delete formData[`overrideMcNumber-${id}`]
      delete formData[`overrideScacCode-${id}`]
      delete formData[`useCustomTruckerToolsDetails-${id}`]
    })
    const payload = getTruckerToolsPayloadStructure(formData as Record<string, string>)
    enableTruckerToolsCarrierApp({
      variables: {
        appType,
        carriers: payload.carriers,
        clientID: payload.clientID,
        clientSecret: payload.clientSecret
      }
    })
  }

  if (isModalEditable && app) {
    return <TruckerToolsCarrierUpdateModal appType={appType} app={app} isOpen={isOpen} onClose={onClose} />
  }

  const getCarrierFields = () => {
    return Object.entries(carriers).map(([groupId, fields]) => (
      <div key={groupId} className="bg-muted p-4 rounded-lg mb-4" role="group" aria-labelledby={`group-${groupId}`}>
        <div className="flex justify-end mb-2">
          {Object.keys(carriers).length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleGroupDelete(groupId)}
              aria-label={`Remove carrier group ${groupId}`}
            >
              Remove
            </Button>
          )}
        </div>
        <div className="space-y-4">
          {fields.map((field: ICustomField) => {
            const errorMessage = errors[field.name]?.message
            const errorId = errorMessage ? `${field.name}-error` : undefined
            return (
              <div key={field.name}>
                <Label htmlFor={field.name} className="block text-sm font-medium mb-1">
                  {field.labelText}{' '}
                  {!!field.isRequired && (
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                  )}
                </Label>
                {field.helpText ? <p className="text-xs text-muted-foreground mb-1">{field.helpText}</p> : null}
                <Input
                  id={field.name}
                  type="text"
                  placeholder={field.placeholder}
                  disabled={field.disabled || false}
                  aria-required={field.isRequired ? 'true' : 'false'}
                  aria-describedby={errorId}
                  aria-invalid={!!errorMessage}
                  {...register(field.name, {
                    required: field.isRequired ? `${field.labelText} is required` : false
                  })}
                />
                {errorMessage ? (
                  <p className="text-sm text-red-500 mt-1" id={errorId} role="alert">
                    {errorMessage}
                  </p>
                ) : null}
              </div>
            )
          })}
          <div className="border rounded-md px-3 py-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor={`useCustomTruckerToolsDetails-${groupId}`} className="text-sm font-medium">
                  Use different Trucker Tools details
                </Label>
                <p className="text-xs text-muted-foreground">
                  Optional: override outbound DOT/MC values sent to Trucker Tools.
                </p>
              </div>
              <Input
                id={`useCustomTruckerToolsDetails-${groupId}`}
                type="checkbox"
                className="h-4 w-4 mt-1"
                checked={customDetailsEnabledByGroup[groupId] ?? false}
                onChange={(event) => handleCustomDetailsToggle(groupId, event.target.checked)}
              />
            </div>

            {customDetailsEnabledByGroup[groupId] ? (
              <div className="mt-3 space-y-3">
                <div>
                  <Label htmlFor={`overrideDotNumber-${groupId}`} className="block text-sm font-medium mb-1">
                    Carrier DOT (Trucker Tools Override){' '}
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                  </Label>
                  <Input
                    id={`overrideDotNumber-${groupId}`}
                    type="text"
                    placeholder="Enter carrier DOT override"
                    {...register(`overrideDotNumber-${groupId}`, {
                      required: customDetailsEnabledByGroup[groupId]
                        ? 'Carrier DOT (Trucker Tools Override) is required'
                        : false
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor={`overrideMcNumber-${groupId}`} className="block text-sm font-medium mb-1">
                    Carrier MC (Trucker Tools Override){' '}
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                  </Label>
                  <Input
                    id={`overrideMcNumber-${groupId}`}
                    type="text"
                    placeholder="Enter carrier MC override"
                    {...register(`overrideMcNumber-${groupId}`, {
                      required: customDetailsEnabledByGroup[groupId]
                        ? 'Carrier MC (Trucker Tools Override) is required'
                        : false
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor={`overrideScacCode-${groupId}`} className="block text-sm font-medium mb-1">
                    SCAC Code (Trucker Tools Override){' '}
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                  </Label>
                  <Input
                    id={`overrideScacCode-${groupId}`}
                    type="text"
                    placeholder="Enter SCAC override"
                    {...register(`overrideScacCode-${groupId}`, {
                      required: customDetailsEnabledByGroup[groupId]
                        ? 'SCAC Code (Trucker Tools Override) is required'
                        : false
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor={`overrideBrokerMC-${groupId}`} className="block text-sm font-medium mb-1">
                    Broker MC (Trucker Tools Override){' '}
                    <span className="text-red-500" aria-hidden="true">
                      *
                    </span>
                  </Label>
                  <Input
                    id={`overrideBrokerMC-${groupId}`}
                    type="text"
                    placeholder="Enter broker MC override"
                    {...register(`overrideBrokerMC-${groupId}`, {
                      required: customDetailsEnabledByGroup[groupId]
                        ? 'Broker MC (Trucker Tools Override) is required'
                        : false
                    })}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    ))
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable Trucker Tools Carrier for Chain</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Enabling this integration requires client credentials and carrier details.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleSaveDetails)} className="space-y-4" noValidate={true}>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="clientID" id="clientID-label">
                Client ID
              </Label>
              <Input
                id="clientID"
                placeholder="Enter Client ID"
                aria-required="true"
                aria-describedby={errors.clientID ? 'clientID-error' : undefined}
                aria-invalid={!!errors.clientID}
                {...register('clientID', { required: 'Client ID is required' })}
              />
              {errors.clientID?.message ? (
                <p className="text-sm text-red-500" id="clientID-error" role="alert">
                  {errors.clientID.message}
                </p>
              ) : null}
            </div>

            <div className="space-y-1">
              <Label htmlFor="clientSecret" id="clientSecret-label">
                Client Secret
              </Label>
              <Input
                id="clientSecret"
                type="password"
                placeholder="Enter Client Secret"
                aria-required="true"
                aria-describedby={errors.clientSecret ? 'clientSecret-error' : undefined}
                aria-invalid={!!errors.clientSecret}
                {...register('clientSecret', { required: 'Client Secret is required' })}
              />
              {errors.clientSecret?.message ? (
                <p className="text-sm text-red-500" id="clientSecret-error" role="alert">
                  {errors.clientSecret.message}
                </p>
              ) : null}
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="block text-sm font-medium">
                  Carrier Configurations{' '}
                  <span className="text-red-500" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMore}
                  aria-label="Add another carrier"
                >
                  Add More
                </Button>
              </div>
              {getCarrierFields()}
            </div>
          </div>

          <DialogFooter className="flex-col space-y-2 sm:space-y-0">
            {!!formError && (
              <p className="text-sm text-red-500 self-start" role="alert">
                {formError}
              </p>
            )}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || isSubmitting}>
                {loading || isSubmitting ? 'Enabling...' : 'Enable'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
